import { ReactNode } from 'react'
import cx from 'classnames'
import { GlassSurface } from '~m/designSystem/components/GlassSurface/GlassSurface'
import { Text } from '~m/designSystem/components/Text/Text'
import styles from './ProgressDetailed.module.css'
import CheckIcon from '@mui/icons-material/Check'
import NotInterestedIcon from '@mui/icons-material/NotInterested'

/** Current status of the task. `null` means not started. */
export type ProgressTaskStatus =
  | null
  | 'in_progress'
  | 'complete'
  | 'skipped'

export interface ProgressTask {
  /** Icon rendered in the task card. Typically, an emoji, image, or icon component. */
  icon: ReactNode
  /** Task title. */
  title: string
  /** Short supporting text describing the task. */
  description: string
  /** Current status of the task. `null` means not started. */
  status: ProgressTaskStatus
  /** actions to be rendered in the task card. */
  actions?: ReactNode
}

export interface ProgressDetailedProps {
  /** Main heading rendered above the progress bar. */
  title: ReactNode
  /** Supporting text rendered under the title. */
  description: ReactNode
  /** Actions rendered in the header. */
  headerActions?: ReactNode
  /** List of tasks rendered as cards beneath the progress bar. */
  tasks: ProgressTask[]
  /** Additional CSS class. */
  className?: string
}

export function ProgressDetailed({
  title,
  description,
  headerActions,
  tasks,
  className,
}: ProgressDetailedProps) {
  const counts = {
    complete: tasks.filter((task) => task.status === 'complete')
      .length,
    in_progress: tasks.filter(
      (task) => task.status === 'in_progress'
    ).length,
    skipped: tasks.filter((task) => task.status === 'skipped')
      .length,
  }

  const progress =
    tasks.length === 0
      ? 0
      : (counts.complete + counts.skipped) / tasks.length

  return (
    <GlassSurface
      component="section"
      variant="panel"
      className={cx(styles.ProgressDetailed, className)}
    >
      <header className={styles.ProgressDetailed__header}>
        {headerActions && (
          <aside
            className={styles.ProgressDetailed__header__actions}
          >
            {headerActions}
          </aside>
        )}
        <Text variant="page-title" component="h2">
          {title}
        </Text>
        <Text
          variant="body"
          className={styles.ProgressDetailed__description}
        >
          {description}
        </Text>
      </header>

      <div className={styles.ProgressDetailed__progressRow}>
        <div
          className={styles.ProgressDetailed__progressBar}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={tasks.length}
          aria-valuenow={counts.complete}
        >
          <div
            className={styles.ProgressDetailed__progressFill}
            style={{ width: `${progress * 100}%` }}
          />
        </div>
        <Text variant="body" component="span">
          {counts.skipped > 0
            ? `${counts.complete} of ${tasks.length} complete (${counts.skipped} skipped)`
            : `${counts.complete} of ${tasks.length} complete`}
        </Text>
      </div>

      <ul className={styles.ProgressDetailed__tasks}>
        {tasks.map((task, index) => (
          <ProgressTask key={index} task={task} />
        ))}
      </ul>
    </GlassSurface>
  )
}

function ProgressTask({ task }: { task: ProgressTask }) {
  return (
    <GlassSurface
      component="li"
      variant="card"
      className={cx(styles.ProgressDetailed__task, {
        [styles.isStatusInProgress]:
          task.status === 'in_progress',
        [styles.isStatusComplete]: task.status === 'complete',
      })}
    >
      <div className={styles.ProgressDetailed__taskIcon}>
        {task.icon}
      </div>
      {task.status === 'complete' && (
        <div
          className={cx(
            styles.ProgressDetailed__taskStatus,
            styles.isStatusComplete
          )}
          aria-label="Complete"
        >
          <CheckIcon fontSize="small" />
        </div>
      )}
      {task.status === 'skipped' && (
        <div
          className={cx(
            styles.ProgressDetailed__taskStatus,
            styles.isStatusSkipped
          )}
          aria-label="Skipped"
        >
          <NotInterestedIcon fontSize="small" />
        </div>
      )}
      <Text variant="section-title" component="h3">
        {task.title}
      </Text>
      <Text
        variant="body"
        className={styles.ProgressDetailed__taskDescription}
      >
        {task.description}
      </Text>
      {task.actions && (
        <div className={styles.ProgressDetailed__taskActions}>
          {task.actions}
        </div>
      )}
    </GlassSurface>
  )
}
